import { useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Heart, Users, Microscope, Lock, X, ExternalLink } from "lucide-react";

const DR_DIPAK_PHOTO = "/assets/images/dr-dipak.png";

const RESEARCH_BARS = [
  { label: "Clinical Depression", nd: 31, nt: 7, diff: "4.4× higher", cite: "Olsson et al. (2019)" },
  { label: "Clinical Anxiety", nd: 31, nt: 14, diff: "2.2× higher", cite: "Olsson et al. (2019)" },
  { label: "Chronic Parenting Stress", nd: 72, nt: 20, diff: "3.6× higher", cite: "Wu et al. (2022)" },
];

const RESEARCH_TABLE = [
  { area: "Clinical Depression", nd: "31%", nt: "7%", diff: "4.4× higher", src: 0 },
  { area: "Clinical Anxiety", nd: "31%", nt: "14%", diff: "2.2× higher", src: 0 },
  { area: "Chronic High Stress", nd: "3.6× more likely", nt: "Baseline", diff: "Massive increase", src: 1 },
  { area: "Anxiety Disorder Diagnosis", nd: "12.2%", nt: "6.9%", diff: "77% higher", src: 1 },
  { area: "Depressive Disorder Diagnosis", nd: "6.0%", nt: "2.8%", diff: "114% higher", src: 1 },
  { area: "Poor Sleep Quality", nd: "Significantly higher prevalence", nt: "Lower prevalence", diff: "Consistently worse sleep", src: 3 },
  { area: "Total Sleep Time", nd: "Shorter sleep duration", nt: "Longer sleep duration", diff: "Chronic sleep loss", src: 3 },
  { area: "Employment Impact", nd: "14.2× more likely to experience employment disruption", nt: "Baseline", diff: "Very large impact", src: 5 },
  { area: "Quality of Life", nd: "Significantly lower", nt: "Higher", diff: "Consistently lower across studies", src: 4 },
  { area: "Social Participation & Leisure", nd: "Reduced social activities and community participation", nt: "Higher participation", diff: "Meaningful reduction", src: 6 },
];

const RESEARCH_SOURCES = [
  { n: 1, title: "Depression and anxiety in parents of children with intellectual and developmental disabilities", authors: "Olsson et al. (2019) · PLOS One", url: "https://journals.plos.org/plosone/article?id=10.1371%2Fjournal.pone.0219888" },
  { n: 2, title: "Psychological distress in parents of children with autism spectrum disorder", authors: "Wu et al. (2022) · PubMed", url: "https://pubmed.ncbi.nlm.nih.gov/35249769/" },
  { n: 3, title: "Multidimensional Burden on Family Caregivers of Adults with ASD", authors: "Dardas & Ahmad · Springer (Review)", url: "https://link.springer.com/article/10.1007/s40489-023-00414-1" },
  { n: 4, title: "Sleep in parents of children with autism spectrum disorders", authors: "Meltzer (2008) · PubMed", url: "https://pubmed.ncbi.nlm.nih.gov/18250091/" },
  { n: 5, title: "Parental Stress and Quality of Life in Parents of Young Children with Autism", authors: "Springer (2024)", url: "https://link.springer.com/article/10.1007/s10578-024-01693-3" },
  { n: 6, title: "Economic burdens on parents of children with autism: a literature review", authors: "Cidav et al. · PubMed", url: "https://pubmed.ncbi.nlm.nih.gov/31709959/" },
  { n: 7, title: "Leisure, Employment, Community Participation, and Quality of Life in Primary Caregivers of Autistic Children", authors: "PubMed (2023)", url: "https://pubmed.ncbi.nlm.nih.gov/37171763/" },
  { n: 8, title: "Systematic Review of the Relationship Between Autism Stigma and Informal Caregiver Mental Health", authors: "Springer", url: "https://link.springer.com/article/10.1007/s10803-018-3835-z" },
];

function BarRow({ value, colorClass, delayMs = 0 }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2.5 rounded-full bg-plum/10 overflow-hidden">
        <div
          className={`h-full ${colorClass} rounded-full kpp-bar-fill`}
          style={{ "--bar-target": `${Math.min(100, value)}%`, animationDelay: `${delayMs}ms` }}
        />
      </div>
      <span className="text-xs font-bold text-inkdark min-w-[36px] text-right">{value}%</span>
    </div>
  );
}

function ResearchModal({ onClose }) {
  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-start sm:items-center justify-center p-4 bg-inkdark/60 backdrop-blur-sm animate-fade-up"
      onClick={onClose}
      data-testid="research-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-soft-lg border border-plum/10"
      >
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-plum/10">
          <div>
            <h3 className="font-heading text-xl sm:text-2xl text-inkdark leading-tight">Parental wellbeing: neurodivergent vs neurotypical caregivers</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close report"
            data-testid="research-modal-close"
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full hover:bg-plum/10 text-inkmid"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-5rem)]">
          <div className="px-6 py-6">
            <div className="overflow-x-auto rounded-2xl border border-plum/10">
              <table className="w-full text-sm">
                <thead className="bg-cream text-[11px] uppercase tracking-widest text-inkmid">
                  <tr>
                    <th className="text-left px-3 py-3 font-bold">Area</th>
                    <th className="text-left px-3 py-3 font-bold">Parents of Neurodivergent Children</th>
                    <th className="text-left px-3 py-3 font-bold">Parents of Neurotypical Children</th>
                    <th className="text-left px-3 py-3 font-bold">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {RESEARCH_TABLE.map((r, i) => (
                    <tr key={r.area} className={i % 2 === 0 ? "bg-white" : "bg-plum/[0.02]"}>
                      <td className="px-3 py-3 font-semibold text-inkdark">{r.area}</td>
                      <td className="px-3 py-3 text-inkdark">{r.nd}</td>
                      <td className="px-3 py-3 text-inkmid">{r.nt}</td>
                      <td className="px-3 py-3">
                        <span className="text-coral font-semibold">{r.diff}</span>
                        <a
                          href={RESEARCH_SOURCES[r.src].url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 align-top ml-1 text-plum hover:text-coral text-[10px] font-bold"
                          title={RESEARCH_SOURCES[r.src].authors}
                        >
                          [{RESEARCH_SOURCES[r.src].n}]
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-7">
              <h4 className="font-heading text-lg text-inkdark mb-3">Sources</h4>
              <p className="text-xs text-inkmid mb-4">Primary peer-reviewed studies behind the numbers above.</p>
              <ol className="space-y-3">
                {RESEARCH_SOURCES.map((s) => (
                  <li key={s.n} className="flex items-start gap-3 bg-cream rounded-2xl px-4 py-3" data-testid={`research-source-${s.n}`}>
                    <span className="text-xs font-bold w-6 h-6 inline-flex items-center justify-center rounded-full bg-plum text-white shrink-0 mt-0.5">{s.n}</span>
                    <div className="flex-1 min-w-0">
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-semibold text-plum hover:text-coral inline-flex items-start gap-1 break-words"
                      >
                        {s.title}
                        <ExternalLink className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      </a>
                      <p className="text-xs text-inkmid mt-0.5">{s.authors}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * "Parenting Made Easier" — research-backed comparison section. Owns
 * its own modal-open state and the table data lives at module scope
 * so it's easy to audit citations.
 */
export default function ResearchSection() {
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <section className="py-16 lg:py-24 relative overflow-hidden" id="research">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        <div className="bg-plum/5 rounded-3xl p-6 sm:p-8 border border-plum/10 shadow-soft order-2 lg:order-1 kpp-reveal" data-reveal data-testid="research-comparison-card">
          <p className="text-xs sm:text-sm font-bold text-inkdark mb-3">Parents of</p>
          <div className="flex flex-wrap gap-4 text-[12px] font-semibold text-inkdark mb-7">
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-coral" /> Neurodivergent children</span>
            <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-mint" /> Neurotypical children</span>
          </div>

          <div className="space-y-7">
            {RESEARCH_BARS.map((b, i) => (
              <div key={b.label} data-testid={`research-bar-${i}`}>
                <p className="text-[11px] font-bold uppercase tracking-widest text-inkdark mb-2">{b.label}</p>
                <div className="space-y-2">
                  <BarRow value={b.nd} colorClass="bg-coral" delayMs={i * 180} />
                  <BarRow value={b.nt} colorClass="bg-mint" delayMs={i * 180 + 90} />
                </div>
                <p className="text-[11px] text-inkmid mt-2"><strong className="text-inkdark">{b.diff}</strong> — {b.cite}</p>
              </div>
            ))}
          </div>

          <p className="mt-7 text-xs text-inkmid leading-relaxed">
            Peer-reviewed evidence from <strong className="text-inkdark">8+ systematic reviews & meta-analyses</strong> spanning thousands of caregivers worldwide.
          </p>

          <button
            type="button"
            onClick={() => setModalOpen(true)}
            data-testid="read-full-report-btn"
            className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-plum hover:bg-plum/90 text-white rounded-full px-7 py-3.5 font-bold text-sm shadow-soft-md hover-lift transition-all"
          >
            Read the Full Report <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="relative order-1 lg:order-2 kpp-reveal" data-reveal>
          <p className="text-xs font-bold uppercase tracking-widest text-plum mb-3" data-testid="research-eyebrow">Parenting Made Easier</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-inkdark">
            We understand what <span className="text-coral">parents</span> really need
          </h2>

          <div className="mt-7 flex items-center gap-4" data-testid="research-author">
            <img
              src={DR_DIPAK_PHOTO}
              alt="Dr. Dipak Kumar — Occupational Therapist"
              loading="lazy"
              className="w-16 h-16 rounded-full object-cover ring-2 ring-white shadow-soft shrink-0"
            />
            <div>
              <p className="font-heading text-lg text-inkdark leading-tight">Dr. Dipak Kumar</p>
              <p className="text-sm text-inkmid leading-tight">Occupational Therapist · Co-founder, Kiddo+</p>
            </div>
          </div>

          <div className="mt-6 space-y-4 text-inkmid text-sm sm:text-base leading-relaxed max-w-xl">
            <p>Research consistently shows that parents of children with developmental challenges face greater emotional, mental, and day-to-day demands than parents of typically developing children.</p>
            <p>Kiddo+ was created to help ease that burden. With personalised developmental insights, expert-designed activities, practical guidance, and real-time progress tracking, Kiddo+ empowers parents to support their child&rsquo;s growth with greater confidence, clarity, and less stress.</p>
          </div>

          <div className="mt-7 grid grid-cols-2 gap-x-6 gap-y-3 max-w-md">
            {[
              { icon: Heart, label: "Designed with empathy" },
              { icon: Users, label: "Created by experts" },
              { icon: Microscope, label: "Backed by science" },
              { icon: Lock, label: "100% data privacy" },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-coral/15 text-coral flex items-center justify-center shrink-0">
                  <b.icon className="w-4 h-4" strokeWidth={2.2} />
                </div>
                <span className="text-sm font-semibold text-inkdark">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {modalOpen && <ResearchModal onClose={() => setModalOpen(false)} />}
    </section>
  );
}
